// import Graphic from "@arcgis/core/Graphic.js";
// import { loadModules } from "esri-loader";

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { locationLatLon, mapCellClass, mapCenter } from "../state/atom/generic";
import { kmlData, proposalDetailsToggle, proposalId } from "../state/atom/proposal";
import {
  requestDetailsToggle,
  requestId,
  requestList,
  requestProposalKmlData,
} from "../state/atom/request";

// OpenLayers Imports:
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import { XYZ } from "ol/source";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature, { FeatureLike } from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { fromLonLat, toLonLat, transformExtent } from "ol/proj";
import { Style, Icon, Fill, Stroke, Text } from "ol/style";
import { defaults as defaultControls } from "ol/control";

export interface Props {
  classList?: string;
}
export const RequestHomeMapOL = ({ classList }: Props) => {
  const request = useRecoilValue(requestList);
  const mapCenterPostion = useRecoilValue(mapCenter);
  const [kmlDataCollection, setKmlData] = useRecoilState(kmlData);
  const [reqProposalKmlData, setRequestProposalKmlData] = useRecoilState(requestProposalKmlData);
  const setProposalDetailsToggle = useSetRecoilState(proposalDetailsToggle);
  const setProposalId = useSetRecoilState(proposalId);
  const setRequestDetailsToggle = useSetRecoilState(requestDetailsToggle);
  const setRequestId = useSetRecoilState(requestId);
  // OpenLayers states (REPLACE ArcGIS states)
  const [map, setMap] = useState<Map | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  // Layer refs (similar to your home.tsx pattern)
  const markerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const kmlLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const proposalLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const centerMarkerRef = useRef<VectorLayer<VectorSource> | null>(null);

  const [mapCellCls, setMapCellCls] = useRecoilState(mapCellClass);
  //const mapCellCls = useRecoilValue(mapCellClass);
  const setlocationLatLon = useSetRecoilState(locationLatLon);
  // Add mouse coordinate state (like your home.tsx)
  const [mouseCoord, setMouseCoord] = useState<{ lat: number; lon: number } | null>(null);
  // Add base maps state
  const [basemaps, setBasemaps] = useState<{ Street: TileLayer; Satellite: TileLayer } | null>(
    null,
  );
  const [activeBasemap, setActiveBasemap] = useState<"Street" | "Satellite">("Satellite");

  // ADD this useEffect for map initialization:
  useEffect(() => {
    if (!mapRef.current || map) return;
    const indiaExtent = [68.111378, 6.554607, 97.395561, 35.674545];
    const indiaExtentTransformed = transformExtent(indiaExtent, "EPSG:4326", "EPSG:3857");

    // Create both base map layers
    // const osmLayer = new TileLayer({
    //   source: new OSM(),
    //   visible: false,
    // });

    const streetLayer = new TileLayer({
      source: new XYZ({
        url: "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
        attributions: "© Google Maps",
      }),
      visible: false,
    });

    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        attributions: "© Google Maps",
        crossOrigin: "anonymous",
        tileSize: 256,
      }),
      visible: true,
    });

    // Store layers for switching
    setBasemaps({
      Street: streetLayer,
      Satellite: satelliteLayer,
    });

    // 1. Create the OpenLayers map (like your home.tsx)
    const olMap = new Map({
      target: mapRef.current,
      layers: [streetLayer, satelliteLayer],
      view: new View({
        // center: fromLonLat([72.22, 23.0]), // Convert lon/lat to OpenLayers coordinates
        // zoom: 4,
        center: fromLonLat([71.5724, 22.6708]), // Center of your bounding box
        zoom: 8, // Much higher zoom level!
        maxZoom: 20,
        minZoom: 5,
        extent: indiaExtentTransformed,
        constrainOnlyCenter: true,
      }),
      controls: defaultControls({
        attribution: false,
        zoom: true,
        rotate: false,
      }),
    });

    olMap.getView().fit(indiaExtentTransformed, {
      size: olMap.getSize(),
      padding: [50, 50, 50, 50],
    });

    // 2. Add your GeoServer WMS layer (like home.tsx but simplified)
    // const wmsLayer = new TileLayer({
    //   source: new TileWMS({
    //     url: "http://141.148.200.50:8080/geoserver/lgeom_lams/wms",
    //     params: {
    //       LAYERS: "lgeom_lams:land_parcels",
    //       TILED: true,
    //       VERSION: "1.1.0",
    //       FORMAT: "image/png",
    //       TRANSPARENT: true,
    //       CQL_FILTER: "INCLUDE", // Include all features
    //     },
    //     serverType: "geoserver",
    //     crossOrigin: "anonymous",
    //   }),
    //   opacity: 0.9,
    //   visible: true,
    // });
    // olMap.addLayer(wmsLayer);

    // 3. Create marker layer for request points
    const markerSource = new VectorSource();
    const markerLayer = new VectorLayer({
      source: markerSource,
      style: getMarkerStyle, // We'll create this function next
    });
    markerLayerRef.current = markerLayer;
    olMap.addLayer(markerLayer);

    // 4. Create KML layer for polygon data
    const kmlSource = new VectorSource();
    const kmlLayer = new VectorLayer({
      source: kmlSource,
      style: new Style({
        fill: new Fill({
          color: "rgba(255, 255, 255, 0)", // Transparent fill
        }),
        stroke: new Stroke({
          color: "rgba(255, 0, 0, 1)", // Red border
          width: 2,
        }),
      }),
    });
    kmlLayerRef.current = kmlLayer;
    olMap.addLayer(kmlLayer);

    // 5. Create proposal layer with labels
    const proposalSource = new VectorSource();
    const proposalLayer = new VectorLayer({
      source: proposalSource,
      style: getProposalStyle, // We'll create this function
    });
    proposalLayerRef.current = proposalLayer;
    olMap.addLayer(proposalLayer);

    // 6. Create center marker layer (for live location)
    const centerMarkerSource = new VectorSource();
    const centerMarkerLayer = new VectorLayer({
      source: centerMarkerSource,
      style: new Style({
        image: new Icon({
          src: "images/live-location-1.gif",
          scale: 0.5,
          anchor: [0.5, 1],
        }),
      }),
    });
    centerMarkerRef.current = centerMarkerLayer;
    olMap.addLayer(centerMarkerLayer);

    // Set the map in state
    setMap(olMap);

    // Cleanup on unmount
    return () => {
      if (olMap) {
        olMap.setTarget(undefined);
      }
    };
  }, []);

  // Update request markers when data changes
  useEffect(() => {
    if (!markerLayerRef.current || !request?.data?.landRequest) return;

    const markerSource = markerLayerRef.current.getSource();
    if (!markerSource) return;

    // Clear existing markers
    markerSource.clear();

    // Add new markers
    request.data.landRequest.forEach((marker: any) => {
      const point = new Point(fromLonLat([marker.locationLon, marker.locationLat]));
      const feature = new Feature({
        geometry: point,
        type: "request",
        id: marker.id,
        requestStatus: marker.requestStatus,
        requestName: marker.requestName,
      });
      markerSource.addFeature(feature);
    });
  }, [request?.data?.landRequest]);

  // Handle map clicks (replaces ArcGIS view.on('click'))
  useEffect(() => {
    if (!map) return;

    const clickHandler = (event: any) => {
      try {
        const coordinate = event.coordinate;
        const lonLat = toLonLat(coordinate);

        if (mapCellCls) {
          // Coordinate picking mode
          setlocationLatLon({
            lat: parseFloat(lonLat[1].toFixed(6)),
            lon: parseFloat(lonLat[0].toFixed(6)),
          });
          setMapCellCls(false);
        } else {
          // Feature selection mode
          const features = map.getFeaturesAtPixel(event.pixel);

          if (features && features.length > 0) {
            // Find the first feature that has an 'id' property
            const feature = features.find((f: FeatureLike) => {
              if (f instanceof Feature) {
                return f.get("id") !== undefined;
              }
              return false;
            });

            if (feature instanceof Feature) {
              const featureId = feature.get("id");
              const featureType = feature.get("type");

              if (featureType === "request" && featureId) {
                setRequestDetailsToggle(true);
                setRequestId(featureId);
              } else if (featureType === "proposal" && featureId) {
                setProposalDetailsToggle(true);
                setProposalId(featureId);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error handling map click:", error);
      }
    };

    map.on("click", clickHandler);

    return () => {
      map.un("click", clickHandler);
    };
  }, [
    map,
    mapCellCls,
    setRequestDetailsToggle,
    setRequestId,
    setProposalDetailsToggle,
    setProposalId,
    setlocationLatLon,
  ]);

  // Update KML data (replaces ArcGIS graphics layer)
  useEffect(() => {
    if (!kmlLayerRef.current || !kmlDataCollection?.features) return;

    const kmlSource = kmlLayerRef.current.getSource();
    if (!kmlSource) return;

    kmlSource.clear();

    kmlDataCollection.features.forEach((feature: any) => {
      const coordinates = feature.geometry.coordinates;
      if (coordinates && coordinates.length > 0) {
        // Convert coordinates from GeoJSON to OpenLayers
        const transformedCoords = coordinates[0].map((coord: [number, number]) =>
          fromLonLat(coord),
        );

        const polygonFeature = new Feature({
          geometry: new Polygon([transformedCoords]),
          type: "kml",
        });
        kmlSource.addFeature(polygonFeature);
      }
    });

    // Zoom to KML extent if we have features
    if (kmlSource.getFeatures().length > 0) {
      const extent = kmlSource.getExtent();
      map?.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 15,
      });
    }
  }, [kmlDataCollection, map]);

  // Update proposal KML data
  useEffect(() => {
    if (!proposalLayerRef.current || !reqProposalKmlData?.length) return;

    const proposalSource = proposalLayerRef.current.getSource();
    if (!proposalSource) return;

    proposalSource.clear();
    if (reqProposalKmlData.length) {
      const features: Feature[] = [];
      reqProposalKmlData.forEach((kmlDataItem: any) => {
        if (kmlDataItem?.kmlData) {
          try {
            const parsedData = JSON.parse(kmlDataItem.kmlData);
            if (parsedData?.features) {
              parsedData.features.forEach((feature: any) => {
                const coordinates = feature.geometry.coordinates;
                if (coordinates && coordinates.length > 0) {
                  // Handle coordinate structure
                  let rings;
                  if (coordinates.length === 1) {
                    rings = coordinates; // Already nested correctly
                  } else {
                    rings = [coordinates]; // Wrap in array
                  }
                  const transformedCoords = rings[0].map((coord: [number, number]) =>
                    fromLonLat(coord),
                  );

                  // Create polygon feature
                  const polygonFeature = new Feature({
                    geometry: new Polygon([transformedCoords]),
                    type: "proposal",
                    id: kmlDataCollection.id,
                    proposalId: kmlDataCollection.proposalId,
                  });

                  // Add polygon feature
                  proposalSource.addFeature(polygonFeature);
                  features.push(polygonFeature);

                  // Create text label feature (centroid)
                  const geom = polygonFeature.getGeometry();
                  if (geom) {
                    // polygon geometry has getInteriorPoint; assert Polygon to access it safely
                    const interiorPoint = (geom as Polygon).getInteriorPoint
                      ? (geom as Polygon).getInteriorPoint()
                      : undefined;
                    if (interiorPoint) {
                      const textFeature = new Feature({
                        geometry: interiorPoint,
                        type: "proposal-label",
                        id: kmlDataCollection.id,
                        proposalId: kmlDataCollection.proposalId,
                      });
                      // add the label feature to the proposal source
                      proposalSource.addFeature(textFeature);
                    }
                  }
                }
              });
            }
          } catch (error) {
            console.error("Error parsing proposal KML data:", error);
          }
        }
      });
    }

    // Zoom to proposal extent if we have features
    if (proposalSource.getFeatures().length > 0) {
      const extent = proposalSource.getExtent();
      map?.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 14,
      });
    }
  }, [reqProposalKmlData, map]);

  // Update map center (replaces ArcGIS view.center/zoom)
  useEffect(() => {
    if (!map || !mapCenterPostion) return;

    const view = map.getView();
    view.setCenter(fromLonLat(mapCenterPostion.center));
    view.setZoom(mapCenterPostion.zoom);

    // Update center marker
    const centerMarkerSource = centerMarkerRef.current?.getSource();
    if (centerMarkerSource) {
      centerMarkerSource.clear();

      if (mapCenterPostion.isShow) {
        const centerPoint = new Point(fromLonLat(mapCenterPostion.center));
        const centerFeature = new Feature({
          geometry: centerPoint,
        });
        centerMarkerSource.addFeature(centerFeature);
      }
    }
  }, [mapCenterPostion, map]);

  // Mouse coordinate display (like your home component)
  useEffect(() => {
    if (!map) return;

    const moveHandler = (event: any) => {
      try {
        const lonLat = toLonLat(event.coordinate);
        setMouseCoord({
          lon: parseFloat(lonLat[0].toFixed(6)),
          lat: parseFloat(lonLat[1].toFixed(6)),
        });
      } catch (err) {
        console.error(err);
      }
    };

    const leaveHandler = () => {
      setMouseCoord(null);
    };

    map.on("pointermove", moveHandler);
    map.getViewport().addEventListener("mouseout", leaveHandler);

    return () => {
      map.un("pointermove", moveHandler);
      map.getViewport().removeEventListener("mouseout", leaveHandler);
    };
  }, [map]);

  useEffect(() => {
    return () => {
      setKmlData({});
      setRequestProposalKmlData([]);
    };
  }, [setKmlData, setRequestProposalKmlData]);

  // Marker style function (replaces ArcGIS picture-marker symbols)
  const getMarkerStyle = (feature: FeatureLike) => {
    // Type guard to check if it's a regular Feature
    if (!(feature instanceof Feature)) {
      return new Style(); // Return empty style for RenderFeature
    }
    const status = feature.get("requestStatus");
    let iconUrl = "images/pending-marker.png";

    switch (status) {
      case "pending":
        iconUrl = "images/pending-marker.png";
        break;
      case "in_progress":
        iconUrl = "images/in-progress-marker.png";
        break;
      case "acquisition":
        iconUrl = "images/acquisition-marker.png";
        break;
      case "proposal_assigned":
        iconUrl = "images/proposal-assigned-marker.png";
        break;
      case "completed":
        iconUrl = "images/completed1234.png";
        break;
    }

    return new Style({
      image: new Icon({
        src: iconUrl,
        scale: 0.8, // Adjust size
        anchor: [0.5, 1], // Anchor at bottom center (like pin)
      }),
    });
  };

  // Proposal polygon style with labels (replaces ArcGIS text symbols)
  const getProposalStyle = (feature: FeatureLike) => {
    // Type guard to check if it's a regular Feature
    if (!(feature instanceof Feature)) {
      return new Style(); // Return empty style for RenderFeature
    }
    const proposalId = feature.get("proposalId");

    // Return an array of styles: one for polygon, one for text
    return [
      // Polygon style
      new Style({
        fill: new Fill({
          color: "rgba(255, 255, 255, 0)", // Transparent
        }),
        stroke: new Stroke({
          color: "rgba(255, 0, 0, 1)", // Red border
          width: 2,
        }),
      }),
      // Text label style
      new Style({
        text: new Text({
          text: proposalId?.toString() || "",
          fill: new Fill({ color: "#fff" }),
          stroke: new Stroke({ color: "#000", width: 2 }),
          font: "bold 12px Arial",
          offsetY: -20, // Position above the polygon
        }),
      }),
    ];
  };

  // Base map switching function
  const changeBaseMap = (mapType: "Street" | "Satellite") => {
    if (!basemaps) return;

    // Hide all base layers
    basemaps.Street.setVisible(false);
    basemaps.Satellite.setVisible(false);

    // Show selected layer
    if (mapType === "Street") {
      basemaps.Street.setVisible(true);
      setActiveBasemap("Street");
    } else {
      basemaps.Satellite.setVisible(true);
      setActiveBasemap("Satellite");
    }
    // console.log(`Switched to ${mapType}`);
  };
  return (
    <>
      <div
        ref={mapRef}
        id="viewDiv"
        style={{ height: "100vh", cursor: mapCellCls ? "cell" : "pointer" }}
        className={classList}
      >
        {/* <button
          className="toogle-map btn-grad"
          onClick={() => {
            const view =
              mapView === "streets-vector" ? "satellite" : "streets-vector";
            setMapView(view);
          }}
        >
          {mapView === "streets-vector" ? "Satellite View" : "Default  View"}
        </button> */}
      </div>
      {/* BaseMap Switch */}
      <div className="map-type">
        <div className="dropdown">
          <div className="nav-item dropdown align-items-center pe-2">
            <Link
              className="nav-link font-weight-bold d-flex text-white dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
              to={""}
            >
              <i className="material-icons opacity-10">public</i>
            </Link>
            <ul
              className="dropdown-menu dropdown-menu-end"
              style={{
                minWidth: "90px",
                background: "#0c0c0c85",
              }}
            >
              <li className="map-type-li">
                <button
                  onClick={() => changeBaseMap("Satellite")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%",
                    padding: "8px 12px",
                    background:
                      activeBasemap === "Satellite" ? "rgba(76, 175, 80, 0.3)" : "transparent",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "white",
                    fontSize: "14px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (activeBasemap !== "Satellite") {
                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeBasemap !== "Satellite") {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                  title="Satellite"
                >
                  <img
                    src="images/satellite.jpeg"
                    alt="Satellite"
                    width="35"
                    height="35"
                    style={{
                      borderRadius: "3px",
                      border:
                        activeBasemap === "Satellite"
                          ? "2px solid #4CAF50"
                          : "1px solid rgba(255,255,255,0.3)",
                    }}
                  />
                  <span>Satellite</span>
                  {activeBasemap === "Satellite" && (
                    <i
                      className="material-icons"
                      style={{
                        marginLeft: "auto",
                        fontSize: "18px",
                        color: "#4CAF50",
                      }}
                    >
                      check_circle
                    </i>
                  )}
                </button>
              </li>

              <li className="map-type-li" style={{ marginTop: "5px" }}>
                <button
                  onClick={() => changeBaseMap("Street")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%",
                    padding: "8px 12px",
                    background:
                      activeBasemap === "Street" ? "rgba(76, 175, 80, 0.3)" : "transparent",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "white",
                    fontSize: "14px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (activeBasemap !== "Street") {
                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeBasemap !== "Street") {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                  title="Street"
                >
                  <img
                    src="images/google-street.jpeg"
                    alt="Street"
                    width="35"
                    height="35"
                    style={{
                      borderRadius: "3px",
                      border:
                        activeBasemap === "Street"
                          ? "2px solid #4CAF50"
                          : "1px solid rgba(255,255,255,0.3)",
                    }}
                  />
                  <span>Street</span>
                  {activeBasemap === "Street" && (
                    <i
                      className="material-icons"
                      style={{
                        marginLeft: "auto",
                        fontSize: "18px",
                        color: "#4CAF50",
                      }}
                    >
                      check_circle
                    </i>
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="map-legend">
        <ul>
          <li>
            <img src="images/pending-marker.png" alt="Pending" />
            Pending
          </li>
          <li>
            <img src="images/in-progress-marker.png" alt="in_progress" />
            In Progress
          </li>
          <li>
            <img src="images/acquisition-marker.png" alt="acquisition" />
            Acquisition
          </li>
          <li>
            <img src="images/proposal-assigned-marker.png" alt="Pending" />
            Proposal Assigned
          </li>
          <li>
            <img src="images/completed1234.png" alt="Completed" />
            Completed
          </li>
        </ul>
      </div>
    </>
  );
};
